import { useCallback, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { ArrowUpRight } from "lucide-react";
import { PRODUCT_COLORS } from "@/components/site/productColors";
import ProductDetailsModal from "@/components/site/ProductDetailsModal";
import ProductLogoTile from "@/components/site/ProductLogoTile";
import { reveal } from "@/components/site/motionPresets";

// Every product carries an explicit access state. Public testing, internal
// tools, previews, and in-development work remain visibly distinct.
//
// `color` drives the logo tile only. Status colour is emerald for publicly
// testable products and neutral grey for internal, preview, and development
// states — access state is a company fact, not a product-brand one.
//
// Account management (including Daubaria deletion) lives on each product's own
// website. This page links out; it does not host product flows.
const PRODUCTS = [
  {
    id: "pocket-peachy",
    name: "Pocket Peachy",
    tagline: "Chibi action-fighter",
    description:
      "A fictional chibi action-fighter where every opening counts. Practice combos, face an AI rival, or play a short campaign slice.",
    logo: "/pocket-peachy-icon-v2.png",
    color: PRODUCT_COLORS.pocketPeachy,
    logoTile: {
      bg: "#FF526B",
      border: `${PRODUCT_COLORS.pocketPeachy}55`,
    },
    logoScale: 1,
    ctaLabel: "Explore Pocket Peachy",
    status: {
      kind: "preview",
      label: "Coming soon · Android",
      tone: "neutral",
    },
    href: "https://pocketpeachy.onevidariadigital.com",
    screenshot: {
      src: "/pocket-peachy-gameplay.webp",
      width: 800,
      height: 450,
      alt: "Pocket Peachy gameplay showing Soraya and Marvic fighting in Quick Battle.",
      position: "center 58%",
    },
  },
  {
    id: "daubaria",
    name: "Daubaria",
    tagline: "Social card-daubing",
    description:
      "A real-time multiplayer card-daubing game. Strictly non-gambling — no real-money bets and no payouts.",
    logo: "/daubaria-logo.png",
    logoTile: { bg: "#911119", border: `${PRODUCT_COLORS.daubaria}50` },
    color: PRODUCT_COLORS.daubaria,
    ctaLabel: "Open Daubaria",
    status: { kind: "shipped", label: "Open testing · Google Play" },
    href: "https://daubaria.onevidariadigital.com",
    screenshot: {
      src: "/daubaria-live.webp",
      width: 1080,
      height: 381,
      alt: "Daubaria mid-game: the ball B 8 has just been called, 24 of 75 drawn, with a straight-line pattern in play.",
      position: "top",
    },
  },
  {
    id: "vidaria",
    name: "Vidaria",
    tagline: "Botanical hub",
    description:
      "A social hub and living database for plant people. Upload a photo and the community helps you name what you are growing.",
    logo: "/vidaria-logo.png",
    color: PRODUCT_COLORS.vidaria,
    logoTile: { bg: "#FFFFFF", border: `${PRODUCT_COLORS.vidaria}55` },
    logoScale: 1,
    ctaLabel: "Open Vidaria",
    status: { kind: "shipped", label: "Alpha · Web" },
    href: "https://vidaria.onevidariadigital.com",
    screenshot: {
      src: "/vidaria-collection.webp",
      width: 975,
      height: 344,
      alt: "Three verified plant records in Vidaria — Alocasia clypeolata, Alocasia sp. Sulu, and Alocasia cfr. clypeolata — each with a photograph and grower's note.",
      position: "center",
    },
  },
  {
    id: "sievepulse",
    name: "SievePulse",
    tagline: "Bulk closure verification",
    description:
      "Pulls road closures from traffic advisories so a person can review and confirm them before they get used.",
    // The supplied icon is an opaque navy square, not a transparent mark, so
    // the tile matches its ground exactly (#002B4F, sampled from the file) and
    // logoScale sits it edge-to-edge. Inset instead, it would read as a navy
    // square floating inside a second tinted tile.
    logo: "/sievepulse-apple-touch-icon.png",
    color: PRODUCT_COLORS.sievepulse,
    logoTile: { bg: "#002B4F", border: `${PRODUCT_COLORS.sievepulse}55` },
    logoScale: 1,
    status: {
      kind: "internal",
      label: "Internal tool · Not public",
      tone: "neutral",
    },
    accessLabel: "Internal use only",
    screenshot: {
      src: "/sievepulse-workspace-preview.png",
      width: 1917,
      height: 875,
      alt: "SievePulse local preview with simulated traffic advisory data: a closure verification queue showing schedules, review states, and batch totals.",
      position: "top",
      caption: "Local preview · Simulated data",
    },
  },
  {
    id: "agos",
    name: "Agos",
    tagline: "Baybayin and Roman keyboard",
    description:
      "An Android keyboard for everyday Roman typing and Traditional Baybayin, with script switching from the keyboard itself. Subscription-based, free for the first seven days.",
    logo: "/agos-logo.svg",
    color: PRODUCT_COLORS.agos,
    // Carries no ground of its own — the tile supplies the navy the mark is
    // drawn for.
    logoTile: { bg: "#152B35", border: `${PRODUCT_COLORS.agos}55` },
    logoScale: 0.9,
    ctaLabel: "Open Agos",
    // The qualifier is load-bearing: Agos is the only shipped product a
    // visitor cannot get into, and "Internal testing" alone reads to a
    // non-developer as just another stage like "Beta". The CTA is still honest
    // — agos.onevidariadigital.com is public — so this badge is the only thing
    // carrying the access constraint.
    status: { kind: "shipped", label: "Internal testing · By invitation" },
    href: "https://agos.onevidariadigital.com",
    screenshot: {
      src: "/agos-baybayin.webp",
      width: 1080,
      height: 382,
      alt: "The Agos Traditional Baybayin keyboard: each glyph key labelled with its Latin equivalent, with kudlit and virama controls on the bottom row.",
      position: "center",
    },
  },
  {
    id: "evi",
    name: "Evi",
    tagline: "Location observation",
    description:
      "An Android app that records location observations exactly as captured and runs a bundled model on the phone to flag patterns worth a second look. Records stay on the device.",
    // Official mark, trimmed to its ink. The gradient is the supplied artwork,
    // not a UI surface, so the no-gradients rule doesn't reach it. The tile
    // supplies Evi's navy so the pale lower stroke has something to sit on.
    logo: "/evi-logo.png",
    color: PRODUCT_COLORS.evi,
    logoTile: { bg: "#0A1E3A", border: `${PRODUCT_COLORS.evi}55` },
    ctaLabel: "Open Evi",
    // Evi is an unreleased prototype placed in the featured product grid. This
    // badge and the copy are what keep the page honest: nothing
    // here may imply Evi can be downloaded, installed, or tried. Evi's own site
    // is the ceiling for what this one claims.
    status: {
      kind: "preview",
      label: "Prototype · Not yet released",
      // Neutral pill: emerald is reserved for released products.
      tone: "neutral",
    },
    // A live website doesn't release the app: the badge above stays
    // "Not yet released" while this link points at Evi's own site.
    href: "https://evi.onevidariadigital.com",
    screenshot: {
      src: "/evi-today.webp",
      width: 720,
      height: 254,
      alt: "Evi's Today screen showing a recorded tracking session from 08:28 to 08:29, lasting 1 minute 12 seconds across 5 observations, marked as not biometrically authenticated.",
      position: "center",
    },
  },

  // In development, grouped by family: -aria products people use, then -Pulse
  // data and signal tools.
  {
    id: "peraria",
    name: "Peraria",
    tagline: "Financial nexus",
    description: "Multi-asset tracking and portfolio intelligence.",
    logo: "/peraria-logo.png",
    color: PRODUCT_COLORS.peraria,
    status: { kind: "in-development", label: "In development" },
  },
  {
    id: "scorepulse",
    name: "ScorePulse",
    tagline: "Live event scoring",
    description: "Real-time scoring and standings for live events.",
    logo: "/scorepulse-logo.png",
    color: PRODUCT_COLORS.scorepulse,
    status: { kind: "in-development", label: "In development" },
  },
  {
    id: "pixelpulse",
    name: "PixelPulse",
    tagline: "Device-native media studio",
    description: "Capture and edit media, published straight from your device.",
    logo: "/pixelpulse-logo.png",
    color: PRODUCT_COLORS.pixelpulse,
    status: { kind: "in-development", label: "In development" },
  },
  {
    id: "stratapulse",
    name: "StrataPulse",
    tagline: "Hazard telemetry",
    description: "Every Philippine hazard. Your exact location.",
    logo: "/stratapulse-logo.png",
    color: PRODUCT_COLORS.stratapulse,
    logoTile: { bg: "#13254A", border: `${PRODUCT_COLORS.stratapulse}55` },
    // The rings are drawn to the edge of their own navy field, so the mark
    // fills the tile rather than sitting inset — otherwise the outer rings
    // collapse into a smudge at 24px.
    logoScale: 1,
    status: { kind: "in-development", label: "In development" },
  },
];

const FEATURED = PRODUCTS.filter((p) => p.status.kind !== "in-development");

// At xl the featured grid is twelve tracks across 1200px, so every row can be
// filled exactly: two cards of 6 (590px), three of 4 (386.67px) or four of 3
// (285px). Rows of three are the default. The array leads with the
// highest-priority products, so leftovers are absorbed by size:
//   count % 3 == 2  → the first two take 6 (5 = 2+3, 8 = 2+3+3)
//   count % 3 == 1  → the last four take 3, a row of four at the end
//                     (4 = 4, 7 = 3+4, 10 = 3+3+4)
//   count == 1      → one card, full width
// Biggest cards first, smallest last. Literal strings so Tailwind generates
// every class.
const XL_SPAN = {
  3: "xl:col-span-3",
  4: "xl:col-span-4",
  6: "xl:col-span-6",
  12: "xl:col-span-12",
};
const xlSpan = (index, count) => {
  if (count === 1) return 12;
  if (count % 3 === 2) return index < 2 ? 6 : 4;
  if (count % 3 === 1) return index >= count - 4 ? 3 : 4;
  return 4;
};
const IN_DEVELOPMENT = PRODUCTS.filter(
  (p) => p.status.kind === "in-development",
);

// Mini-strip content for the featured cards — the card already carries the
// description, so these three terms are the scannable detail. Full
// documentation lives on each product's own website.
const DAUBARIA_FEATURES = [
  {
    term: "Real-time multiplayer",
    detail: "Public rooms or private sessions.",
  },
  {
    term: "Fast sync",
    detail: "Built to keep every card in step.",
  },
  {
    term: "18+ amusement only",
    detail: "No real-money bets, no payouts.",
  },
];

const VIDARIA_FEATURES = [
  {
    term: "Community identification",
    detail: "Upload a photo, name what you grow.",
  },
  {
    term: "Botanical database",
    detail: "Living reference built by growers.",
  },
  {
    term: "Collections",
    detail: "Catalogue plants, follow growers.",
  },
];

const SIEVEPULSE_FEATURES = [
  {
    term: "Bulk review",
    detail: "Long advisories into a simple checklist.",
  },
  {
    term: "Checked by people",
    detail: "Review road, schedule, and location.",
  },
  {
    term: "Clear queue",
    detail: "See what is verified and what needs work.",
  },
];

// ⚠️ The privacy claim is deliberately not one of these, even though for a
// keyboard it is the strongest thing on offer. Agos states it in two
// sentences — the second ("The app receiving your text has its own data
// practices.") is load-bearing, and `detail` is line-clamp-2, so it would be
// truncated away and leave an unqualified absolute claim about software that
// sees everything typed. The full, qualified version lives in policyContent.
// ⚠️ Keep each detail under ~40 characters, and under ~35 for the middle
// column, which is the narrowest of the three. `detail` is line-clamp-2: the
// other products run 31-41 characters and fill exactly two lines, so anything
// materially longer is silently cut. Measured, not guessed.
const AGOS_FEATURES = [
  {
    term: "Two scripts, one keyboard",
    detail: "Switch scripts from the keyboard itself.",
  },
  {
    term: "Latin labels throughout",
    detail: "With kudlit and virama controls.",
  },
  {
    term: "Set your own rhythm",
    detail: "Light or dark, adjustable row spacing.",
  },
];

const POCKET_PEACHY_FEATURES = [
  {
    term: "Ten playable fighters",
    detail: "Each rival has a different style.",
  },
  {
    term: "Offline by design",
    detail: "No accounts, ads, or active purchases.",
  },
  {
    term: "Three game modes",
    detail: "Practice, Quick Battle, short campaign.",
  },
];

// The third entry is a boundary, not a feature, and must stay one. Evi's model
// was trained on synthetic data only and flags patterns for a person to review;
// it does not establish where someone was or whether anything was falsified.
// Do not turn it into a capability claim.
const EVI_FEATURES = [
  {
    term: "Recorded, not interpreted",
    detail: "What the phone captured stays separate.",
  },
  {
    term: "The model runs on the phone",
    detail: "No internet permission, no cloud.",
  },
  {
    term: "Flags, not findings",
    detail: "A pattern to review, not proof.",
  },
];

const CARD_FEATURES = {
  daubaria: DAUBARIA_FEATURES,
  vidaria: VIDARIA_FEATURES,
  sievepulse: SIEVEPULSE_FEATURES,
  agos: AGOS_FEATURES,
  "pocket-peachy": POCKET_PEACHY_FEATURES,
  evi: EVI_FEATURES,
};

/** Block A — featured products. Premium corporate card: visual on top,
 *  serif title, a desktop feature strip, zero-reload details, outbound link. */
function ProductCard({ product, index, span = 4, onOpenDetails }) {
  // Only a 6- or 12-track card (590px+) is wide enough for three features
  // across; 4- and 3-track cards (387px, 285px) stack them.
  const wide = span >= 6;
  const {
    id,
    name,
    tagline,
    description,
    status,
    href,
    screenshot,
    ctaLabel,
    accessLabel,
  } = product;
  const features = (CARD_FEATURES[id] || []).slice(0, 3);
  // Emerald means publicly testable (design_guidelines → components.statusPill).
  // Internal and unreleased products get the neutral pill, so colour never
  // implies public access where none exists.
  const publiclyAvailable = status.tone !== "neutral";

  return (
    <motion.article
      data-testid={`ecosystem-card-${id}`}
      {...reveal({ y: 24, duration: 0.55, delay: index * 0.06 })}
      className={`card-premium product-card flex h-full min-w-0 flex-col ${XL_SPAN[span]}`}
    >
      {screenshot ? (
        // Full-bleed, flush to the card edges. The aspect box reserves the
        // space before the file arrives, so nothing shifts on load. The bottom
        // hairline is load-bearing rather than decorative: Vidaria's capture is
        // a dark UI whose bottom edge sits within a few RGB units of
        // --surface-1, so without a rule the image has no visible end.
        <figure className="relative">
          <img
            src={screenshot.src}
            alt={screenshot.alt}
            width={screenshot.width}
            height={screenshot.height}
            loading="lazy"
            decoding="async"
            className="aspect-[2.83/1] w-full border-b border-line object-cover"
            style={{ objectPosition: screenshot.position }}
          />
          {screenshot.caption && (
            <figcaption className="absolute bottom-2 right-2 max-w-[calc(100%-1rem)] rounded-sm bg-black/80 px-2 py-1 text-[10px] leading-relaxed text-white/90">
              {screenshot.caption}
            </figcaption>
          )}
        </figure>
      ) : null}

      <div className="flex flex-1 flex-col p-6 sm:p-8">
        <div className="flex items-start gap-4">
          <ProductLogoTile product={product} size={48} />
          <div className="min-w-0 flex-1">
            <h3
              className="font-serif-display text-[1.7rem] font-medium tracking-tight text-cream"
              data-testid={`ecosystem-card-name-${id}`}
            >
              {name}
            </h3>
            <p className="mt-1 text-sm text-white/70">{tagline}</p>
          </div>
        </div>

        <div className="mt-5">
          <span
            className={`inline-flex items-center gap-2 rounded-full border px-3 py-1 text-[10px] font-medium uppercase tracking-[0.14em] ${
              publiclyAvailable
                ? "border-[#10B981]/30 bg-[#10B981]/10 text-[#10B981]"
                : "border-line-strong text-white/70"
            }`}
            data-testid={`ecosystem-status-${id}`}
          >
            {publiclyAvailable ? (
              <span className="h-1.5 w-1.5 rounded-full bg-[#10B981]" />
            ) : null}
            {status.label}
          </span>
        </div>

        <p className="mt-5 text-sm leading-relaxed text-white/80 sm:text-base">
          {description}
        </p>

        {/* Three across only where the card is wide enough to hold it
            (~590px+). Below xl that is decided by breakpoint: single-column
            cards at sm-lg are 592-975px, two-column cards at lg are 462-589px
            and stack. At xl the card's own span decides: 6+ tracks (590px)
            go three across, 4 and 3 tracks (387px, 285px) stack. Three-across
            in a 387px card leaves 74-91px per column and every detail
            truncates. */}
        {features.length > 0 ? (
          <ul
            className={`mt-6 hidden grid-cols-1 gap-0 border-t border-line sm:grid sm:grid-cols-3 sm:divide-x sm:divide-line lg:grid-cols-1 lg:divide-x-0 ${
              wide ? "xl:grid-cols-3 xl:divide-x" : ""
            }`}
          >
            {features.map((f) => (
              <li
                key={f.term}
                className={`py-4 sm:px-4 sm:first:pl-0 sm:last:pr-0 lg:px-0 ${
                  wide ? "xl:px-4 xl:first:pl-0 xl:last:pr-0" : ""
                }`}
              >
                <p className="text-[13px] font-semibold leading-snug text-white/90">
                  {f.term}
                </p>
                <p className="mt-1 line-clamp-2 text-[13px] leading-relaxed text-white/65">
                  {f.detail}
                </p>
              </li>
            ))}
          </ul>
        ) : null}

        <div className="mt-7 flex flex-1 flex-wrap items-end gap-3">
          <button
            type="button"
            onClick={onOpenDetails}
            data-testid={`ecosystem-details-${id}`}
            className="inline-flex min-h-[44px] items-center justify-center rounded-full border border-line-strong px-5 py-3 text-sm font-medium text-white/80 transition-colors hover:border-white/25 hover:bg-surface-2 hover:text-white"
          >
            Details
          </button>
          {href ? (
            <a
              href={href}
              target="_blank"
              rel="noopener noreferrer"
              data-testid={`ecosystem-visit-${id}`}
              className="group inline-flex min-h-[44px] items-center gap-2 rounded-full bg-[#10B981] px-5 py-3 text-sm font-semibold text-[#06231A] transition-colors hover:bg-[#34D399]"
            >
              {ctaLabel}
              <ArrowUpRight
                size={15}
                className="transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5"
              />
            </a>
          ) : accessLabel ? (
            <p
              data-testid={`ecosystem-unavailable-${id}`}
              className="py-3 text-sm text-white/70"
            >
              {accessLabel}
            </p>
          ) : !publiclyAvailable ? (
            <p
              data-testid={`ecosystem-unavailable-${id}`}
              className="py-3 text-sm text-white/70"
            >
              Not yet available
            </p>
          ) : null}
        </div>
      </div>
    </motion.article>
  );
}

/** Block B — one quiet row per product. No hover, no affordance, no link. */
function PipelineRow({ product, index }) {
  const { id, name, description, status } = product;

  return (
    <motion.li
      data-testid={`ecosystem-row-${id}`}
      {...reveal({ y: 16, duration: 0.45, delay: index * 0.05 })}
      className="flex flex-col gap-2 border-t border-line py-5 sm:flex-row sm:items-center sm:gap-6"
    >
      <div className="flex items-center gap-3 sm:w-56 sm:flex-shrink-0">
        <ProductLogoTile product={product} size={24} />
        <span className="font-display text-base text-white">{name}</span>
      </div>
      <p className="flex-1 text-sm text-white/70">{description}</p>
      <span
        className="text-[10px] uppercase tracking-[0.14em] text-white/60 sm:flex-shrink-0"
        data-testid={`ecosystem-status-${id}`}
      >
        {status.label}
      </span>
    </motion.li>
  );
}

export default function Ecosystem() {
  const [selectedProduct, setSelectedProduct] = useState(null);
  const closeProductDetails = useCallback(() => setSelectedProduct(null), []);

  return (
    <>
      <section
        id="ecosystem"
        data-testid="ecosystem-section"
        aria-label="Products built by OneVidaria Digital"
        className="relative pb-12 pt-28 sm:pb-16 sm:pt-36"
      >
        <div className="mx-auto max-w-7xl px-6 lg:px-10">
          <div className="mb-14 flex flex-col gap-8 lg:flex-row lg:items-end lg:justify-between">
            <div>
              <h2
                data-testid="ecosystem-heading"
                className="font-serif-display text-4xl font-medium tracking-tight text-cream sm:text-5xl lg:text-6xl"
              >
                Products in motion.
              </h2>
            </div>
            <p className="max-w-md text-sm leading-relaxed text-white/70 sm:text-base">
              Six products are in testing, preview, or internal use. Four more
              are in active development.
            </p>
          </div>

          {/* Explicit parent-company statement for screen readers and AI parsers */}
          <p className="sr-only">
            Daubaria, Vidaria, Peraria, ScorePulse, PixelPulse, SievePulse,
            StrataPulse, Agos, Pocket Peachy, and Evi are the ten applications
            developed, owned, and operated by OneVidaria Digital.
          </p>

        {/* Block A — testing, internal, and preview products. One column, two
            at lg, twelve tracks
            at xl with spans from xlSpan(), so every xl row fills the full
            width. At six cards: two rows of three 387px cards. Not three
            columns at lg: at a 1024px viewport that gives ~301px cards. At
            lg the two-column layout keeps every card comfortably readable. */}
          <div
            className="grid grid-cols-1 gap-5 lg:grid-cols-2 xl:grid-cols-12"
            data-testid="ecosystem-grid"
          >
            {FEATURED.map((p, i) => (
              <ProductCard
                key={p.id}
                product={p}
                index={i}
                span={xlSpan(i, FEATURED.length)}
                onOpenDetails={() => setSelectedProduct(p)}
              />
            ))}
          </div>

        {/* Block B — everything else, deliberately quiet */}
          <div className="mt-20">
            <h3 className="font-display text-lg font-normal tracking-tight text-white/70">
              In development
            </h3>
            <ul
              className="mt-6 border-b border-line"
              data-testid="ecosystem-pipeline"
            >
              {IN_DEVELOPMENT.map((p, i) => (
                <PipelineRow key={p.id} product={p} index={i} />
              ))}
            </ul>
          </div>
        </div>
      </section>

      <AnimatePresence>
        {selectedProduct ? (
          <ProductDetailsModal
            product={selectedProduct}
            features={CARD_FEATURES[selectedProduct.id] || []}
            onClose={closeProductDetails}
          />
        ) : null}
      </AnimatePresence>
    </>
  );
}
